import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Grid,
  IconButton,
  Autocomplete,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useNotify } from 'react-admin';
import { catalogAttributes, updateCatalogAttributes } from '@src/apis/reware/catalog';
import type { ValidCodeNameType } from '@src/types';

interface CatalogAttributesModalProps {
  open: boolean;
  onClose: () => void;
  partnerCode: string;
  token: string;
}

interface AttributeChange {
  code: string;
  name: string;
}

/**
 * Modal for managing catalog attributes (Color, Size, Season, Gender)
 * Allows bulk updates to code/name mappings
 */
export const CatalogAttributesModal = ({
  open,
  onClose,
  partnerCode,
  token,
}: CatalogAttributesModalProps) => {
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Attribute states
  const [validColors, setValidColors] = useState<ValidCodeNameType[]>([]);
  const [validSizes, setValidSizes] = useState<ValidCodeNameType[]>([]);
  const [validSeasons, setValidSeasons] = useState<ValidCodeNameType[]>([]);
  const [validGenders, setValidGenders] = useState<ValidCodeNameType[]>([]);

  // Input states for new/edited attributes
  const [colorCode, setColorCode] = useState('');
  const [colorName, setColorName] = useState('');
  const [sizeCode, setSizeCode] = useState('');
  const [sizeName, setSizeName] = useState('');
  const [seasonCode, setSeasonCode] = useState('');
  const [seasonName, setSeasonName] = useState('');
  const [genderCode, setGenderCode] = useState('');
  const [genderName, setGenderName] = useState('');

  // Track changes for bulk update
  const [colorChanges, setColorChanges] = useState<AttributeChange[]>([]);
  const [sizeChanges, setSizeChanges] = useState<AttributeChange[]>([]);
  const [seasonChanges, setSeasonChanges] = useState<AttributeChange[]>([]);
  const [genderChanges, setGenderChanges] = useState<AttributeChange[]>([]);

  const loadAttributes = async () => {
    setLoading(true);
    try {
      const attributes = await catalogAttributes(partnerCode, token);
      setValidColors(attributes.validColors || []);
      setValidSizes(attributes.validSizes || []);
      setValidSeasons(attributes.validSeasons || []);
      setValidGenders(attributes.validGenders || []);
    } catch (error) {
      notify('Failed to load catalog attributes', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Fetch attributes when modal opens
  useEffect(() => {
    if (open && partnerCode && token) {
      void loadAttributes();
    }
  }, [open, partnerCode, token]);

  // Handle code change and auto-populate display name
  const handleCodeChange = (
    type: 'color' | 'size' | 'season' | 'gender',
    code: string | null
  ) => {
    if (!code) {
      // Clear both fields if code is cleared
      switch (type) {
        case 'color':
          setColorCode('');
          setColorName('');
          break;
        case 'size':
          setSizeCode('');
          setSizeName('');
          break;
        case 'season':
          setSeasonCode('');
          setSeasonName('');
          break;
        case 'gender':
          setGenderCode('');
          setGenderName('');
          break;
      }
      return;
    }

    // Update code state
    switch (type) {
      case 'color':
        setColorCode(code);
        break;
      case 'size':
        setSizeCode(code);
        break;
      case 'season':
        setSeasonCode(code);
        break;
      case 'gender':
        setGenderCode(code);
        break;
    }

    // Auto-populate display name if mapping exists
    const getValidOptions = () => {
      switch (type) {
        case 'color': return validColors;
        case 'size': return validSizes;
        case 'season': return validSeasons;
        case 'gender': return validGenders;
        default: return [];
      }
    };

    const validOptions = getValidOptions();
    const selectedOption = validOptions.find(option => option.code === code);

    if (selectedOption) {
      // Auto-populate and don't track as change yet (just restoring existing mapping)
      switch (type) {
        case 'color':
          setColorName(selectedOption.name);
          break;
        case 'size':
          setSizeName(selectedOption.name);
          break;
        case 'season':
          setSeasonName(selectedOption.name);
          break;
        case 'gender':
          setGenderName(selectedOption.name);
          break;
      }
    } else {
      // Clear display name if no mapping exists
      switch (type) {
        case 'color':
          setColorName('');
          break;
        case 'size':
          setSizeName('');
          break;
        case 'season':
          setSeasonName('');
          break;
        case 'gender':
          setGenderName('');
          break;
      }
    }
  };

  // Handle display name change and track changes
  const handleDisplayChange = (
    type: 'color' | 'size' | 'season' | 'gender',
    displayValue: string
  ) => {
    // Update the display field state
    switch (type) {
      case 'color':
        setColorName(displayValue);
        break;
      case 'size':
        setSizeName(displayValue);
        break;
      case 'season':
        setSeasonName(displayValue);
        break;
      case 'gender':
        setGenderName(displayValue);
        break;
    }

    // Get current code for this type
    const getCurrentCode = () => {
      switch (type) {
        case 'color': return colorCode;
        case 'size': return sizeCode;
        case 'season': return seasonCode;
        case 'gender': return genderCode;
        default: return '';
      }
    };

    const currentCode = getCurrentCode();

    // Track changes if we have both code and display
    if (currentCode && displayValue) {
      const newChange: AttributeChange = { code: currentCode, name: displayValue };

      switch (type) {
        case 'color':
          setColorChanges((prev) => {
            const existing = prev.findIndex(c => c.code === currentCode);
            if (existing >= 0) {
              const updated = [...prev];
              updated[existing] = newChange;
              return updated;
            }
            return [...prev, newChange];
          });
          break;
        case 'size':
          setSizeChanges((prev) => {
            const existing = prev.findIndex(c => c.code === currentCode);
            if (existing >= 0) {
              const updated = [...prev];
              updated[existing] = newChange;
              return updated;
            }
            return [...prev, newChange];
          });
          break;
        case 'season':
          setSeasonChanges((prev) => {
            const existing = prev.findIndex(c => c.code === currentCode);
            if (existing >= 0) {
              const updated = [...prev];
              updated[existing] = newChange;
              return updated;
            }
            return [...prev, newChange];
          });
          break;
        case 'gender':
          setGenderChanges((prev) => {
            const existing = prev.findIndex(c => c.code === currentCode);
            if (existing >= 0) {
              const updated = [...prev];
              updated[existing] = newChange;
              return updated;
            }
            return [...prev, newChange];
          });
          break;
      }
    }
  };

  const handleSave = async () => {
    if (
      colorChanges.length === 0 &&
      sizeChanges.length === 0 &&
      seasonChanges.length === 0 &&
      genderChanges.length === 0
    ) {
      notify('No changes to save', { type: 'info' });
      return;
    }

    setSaving(true);
    try {
      const result = await updateCatalogAttributes({
        partnerCode,
        token,
        colorUpdates: colorChanges.length > 0 ? colorChanges : undefined,
        sizeUpdates: sizeChanges.length > 0 ? sizeChanges : undefined,
        seasonUpdates: seasonChanges.length > 0 ? seasonChanges : undefined,
        genderUpdates: genderChanges.length > 0 ? genderChanges : undefined,
      });

      if (result.success) {
        const counts = [
          result.colorUpdatedCount && `${result.colorUpdatedCount} colors`,
          result.sizeUpdatedCount && `${result.sizeUpdatedCount} sizes`,
          result.seasonUpdatedCount && `${result.seasonUpdatedCount} seasons`,
          result.genderUpdatedCount && `${result.genderUpdatedCount} genders`,
        ]
          .filter(Boolean)
          .join(', ');

        notify(`Updated ${counts}`, { type: 'success' });

        // Clear changes
        setColorChanges([]);
        setSizeChanges([]);
        setSeasonChanges([]);
        setGenderChanges([]);

        // Reload attributes
        await loadAttributes();
      } else {
        notify(result.error || 'Update failed', { type: 'error' });
      }
    } catch (error) {
      notify('Failed to update attributes', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setColorChanges([]);
    setSizeChanges([]);
    setSeasonChanges([]);
    setGenderChanges([]);
    setColorCode('');
    setColorName('');
    setSizeCode('');
    setSizeName('');
    setSeasonCode('');
    setSeasonName('');
    setGenderCode('');
    setGenderName('');
    onClose();
  };

  const hasChanges =
    colorChanges.length > 0 ||
    sizeChanges.length > 0 ||
    seasonChanges.length > 0 ||
    genderChanges.length > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Catalog Attributes</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Typography>Loading attributes...</Typography>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Manage valid code/name mappings for catalog attributes. Select a code and modify the display name to stage changes.
            </Typography>

            {/* Color Attributes */}
            <Box sx={{ mt: 3, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Color Attributes
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={validColors.map(c => c.code)}
                    value={colorCode || null}
                    onChange={(_, value) => handleCodeChange('color', value)}
                    renderInput={params => (
                      <TextField {...params} label="Code" placeholder="Search for color code" />
                    )}
                    freeSolo
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Display Name"
                    value={colorName}
                    onChange={e => handleDisplayChange('color', e.target.value)}
                    placeholder="e.g., Red"
                  />
                </Grid>
              </Grid>
              {colorChanges.length > 0 && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Pending changes:
                  {' '}
                  {colorChanges.map(c => `${c.code}=${c.name}`).join(', ')}
                </Alert>
              )}
            </Box>

            {/* Size Attributes */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Size Attributes
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={validSizes.map(s => s.code)}
                    value={sizeCode || null}
                    onChange={(_, value) => handleCodeChange('size', value)}
                    renderInput={params => (
                      <TextField {...params} label="Code" placeholder="Search for size code" />
                    )}
                    freeSolo
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Display Name"
                    value={sizeName}
                    onChange={e => handleDisplayChange('size', e.target.value)}
                    placeholder="e.g., Medium"
                  />
                </Grid>
              </Grid>
              {sizeChanges.length > 0 && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Pending changes:
                  {' '}
                  {sizeChanges.map(c => `${c.code}=${c.name}`).join(', ')}
                </Alert>
              )}
            </Box>

            {/* Season Attributes */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Season Attributes
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={validSeasons.map(s => s.code)}
                    value={seasonCode || null}
                    onChange={(_, value) => handleCodeChange('season', value)}
                    renderInput={params => (
                      <TextField {...params} label="Code" placeholder="Search for season code" />
                    )}
                    freeSolo
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Display Name"
                    value={seasonName}
                    onChange={e => handleDisplayChange('season', e.target.value)}
                    placeholder="e.g., Spring/Summer 2022"
                  />
                </Grid>
              </Grid>
              {seasonChanges.length > 0 && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Pending changes:
                  {' '}
                  {seasonChanges.map(c => `${c.code}=${c.name}`).join(', ')}
                </Alert>
              )}
            </Box>

            {/* Gender Attributes */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Gender Attributes
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={validGenders.map(g => g.code)}
                    value={genderCode || null}
                    onChange={(_, value) => handleCodeChange('gender', value)}
                    renderInput={params => (
                      <TextField {...params} label="Code" placeholder="Search for gender code" />
                    )}
                    freeSolo
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Display Name"
                    value={genderName}
                    onChange={e => handleDisplayChange('gender', e.target.value)}
                    placeholder="e.g., Men's"
                  />
                </Grid>
              </Grid>
              {genderChanges.length > 0 && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Pending changes:
                  {' '}
                  {genderChanges.map(c => `${c.code}=${c.name}`).join(', ')}
                </Alert>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!hasChanges || saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
